// Thanh float toàn cục cho job "Cắt highlight" chạy ngầm.
// Mount 1 lần ở cấp cao (trong AuthProvider) → hiện trên MỌI trang, sống qua
// reload/tắt-mở app cho tới khi user bấm "Thoát". Thay thế PendingClipsWatcher
// + toast "Highlight đã xong" cũ.
//
// UI: pill gọn ở TRÊN CÙNG căn giữa (né tai thỏ nhờ safe-area). Nhiều job →
// gộp "Đang cắt N video", bấm để bung danh sách chi tiết. Mỗi job có:
//  - progress emerald
//  - Stop (huỷ hẳn job + xoá file server)
//  - Tải về (khi xong; tự tải 1 lần + cho tải lại)
//  - Thoát (đang cắt = thu gọn tạm, job vẫn chạy; xong = gỡ khỏi danh sách)
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Scissors, DownloadSimple, X, Stop, CaretDown, CaretUp,
  CheckCircle, WarningCircle, CircleNotch, Lightning,
} from "@phosphor-icons/react";

import { api } from "../lib/api";
import { downloadVideo } from "../lib/downloadVideo";
import { subscribe, getJobs, updateJob, removeJob } from "../lib/clipJobs";
import { useAuth } from "../contexts/AuthContext";

export default function ClipJobsFloat() {
  const { user } = useAuth();
  const jobs = useSyncExternalStore(subscribe, getJobs);
  const [expanded, setExpanded] = useState(false);
  const pollingRef = useRef(false);

  // ── Poll trạng thái các job đang xử lý ──
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;

    const tick = async () => {
      if (pollingRef.current) return;
      const active = getJobs().filter((j) => j.status === "processing");
      if (active.length === 0) return;
      pollingRef.current = true;
      try {
        for (const job of active) {
          try {
            const r = await api.get(`/vod/personal-clip/${job.id}`);
            const d = r?.data || {};
            if (d.status === "ready" && d.download_url) {
              if (!job.downloaded) {
                updateJob(job.id, {
                  status: "ready", progress: 100, download_url: d.download_url,
                  hidden: false, downloaded: true,
                });
                downloadVideo({
                  url: d.download_url, filename: job.filename, toast,
                  toastId: `clip-dl-${job.id}`,
                });
              } else {
                updateJob(job.id, {
                  status: "ready", progress: 100, download_url: d.download_url, hidden: false,
                });
              }
            } else if (d.status === "failed") {
              updateJob(job.id, { status: "failed", hidden: false });
            } else if (d.status === "cancelled") {
              removeJob(job.id);
            } else {
              updateJob(job.id, { progress: Math.max(3, Math.round(d.progress || 0)) });
            }
          } catch (err) {
            if (err?.response?.status === 404) removeJob(job.id);
            /* lỗi mạng tạm thời → giữ job, thử lại lần sau */
          }
        }
      } finally {
        if (!cancelled) pollingRef.current = false;
      }
    };

    tick();
    const iv = setInterval(() => { if (!cancelled) tick(); }, 1500);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user]);

  // Job hiển thị = không bị thu gọn (hidden chỉ áp khi còn đang cắt).
  const visible = jobs.filter((j) => !j.hidden);
  const processing = visible.filter((j) => j.status === "processing");
  const ready = visible.filter((j) => j.status === "ready");
  const failed = visible.filter((j) => j.status === "failed");

  if (visible.length === 0) return null;

  const overall = processing.length
    ? Math.round(processing.reduce((s, j) => s + (j.progress || 0), 0) / processing.length)
    : 100;

  let summary;
  if (processing.length > 0) {
    summary = processing.length === 1 ? "Đang cắt highlight…" : `Đang cắt ${processing.length} video…`;
  } else if (failed.length > 0 && ready.length === 0) {
    summary = "Cắt highlight thất bại";
  } else {
    summary = ready.length === 1 ? "Highlight đã sẵn sàng 🎬" : `${ready.length} highlight đã sẵn sàng 🎬`;
  }

  const stopJob = async (job) => {
    try { await api.post(`/vod/personal-clip/${job.id}/cancel`); } catch { /* best-effort */ }
    removeJob(job.id);
    toast("Đã huỷ cắt highlight.");
  };

  const exitJob = (job) => {
    // Đang cắt → thu gọn (job vẫn chạy ngầm, xong sẽ tự hiện lại). Xong/lỗi → gỡ.
    if (job.status === "processing") updateJob(job.id, { hidden: true });
    else removeJob(job.id);
  };

  const redownload = async (job) => {
    let url = job.download_url;
    try {
      const r = await api.get(`/vod/personal-clip/${job.id}`);
      if (r?.data?.download_url) url = r.data.download_url;
    } catch { /* dùng url cũ */ }
    if (!url) { toast.error("Không lấy được link tải. Thử lại nhé."); return; }
    downloadVideo({ url, filename: job.filename, toast, toastId: `clip-dl-${job.id}` });
  };

  const anyProcessing = processing.length > 0;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[2000] w-[min(94vw,440px)] px-2 pointer-events-none"
      style={{ top: "max(0.5rem, calc(var(--pl-safe-top, env(safe-area-inset-top, 0px)) + 0.375rem))" }}
      data-testid="clip-jobs-float"
    >
      <motion.div
        initial={{ y: -24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="pointer-events-auto rounded-2xl bg-[#0B1220]/95 backdrop-blur-md border border-white/12 shadow-2xl overflow-hidden"
      >
        {/* Pill tóm tắt */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left"
          data-testid="clip-jobs-summary"
        >
          <span className="shrink-0">
            {anyProcessing
              ? <CircleNotch size={17} weight="bold" className="text-[#A8FF00] animate-spin" />
              : failed.length && !ready.length
                ? <WarningCircle size={17} weight="fill" className="text-rose-400" />
                : <CheckCircle size={17} weight="fill" className="text-emerald-400" />}
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[12.5px] font-bold text-white truncate">{summary}</span>
            {anyProcessing && (
              <span className="mt-1 block h-1.5 rounded-full bg-white/12 overflow-hidden">
                <span
                  className="block h-full bg-emerald-400 transition-all duration-500"
                  style={{ width: `${overall}%` }}
                  data-testid="clip-jobs-overall-bar"
                />
              </span>
            )}
          </span>
          {anyProcessing && <span className="shrink-0 text-[12px] font-black tabular-nums text-emerald-300">{overall}%</span>}
          <span className="shrink-0 text-white/45">
            {expanded ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
          </span>
        </button>

        {/* Chi tiết từng job */}
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden border-t border-white/8"
            >
              <div className="max-h-[50vh] overflow-y-auto divide-y divide-white/6">
                {visible.map((job) => (
                  <div key={job.id} className="px-3.5 py-2.5" data-testid={`clip-job-${job.id}`}>
                    <div className="flex items-center gap-2">
                      {job.autoMode
                        ? <Lightning size={13} weight="fill" className="text-[#A8FF00] shrink-0" />
                        : <Scissors size={13} weight="bold" className="text-[#A8FF00] shrink-0" />}
                      <span className="flex-1 min-w-0 text-[12px] font-semibold text-white/90 truncate">{job.title}</span>
                      {job.status === "processing" && <span className="text-[11px] font-black tabular-nums text-emerald-300">{job.progress || 0}%</span>}
                    </div>

                    {job.status === "processing" && (
                      <div className="mt-1.5 h-1.5 rounded-full bg-white/12 overflow-hidden">
                        <div className="h-full bg-emerald-400 transition-all duration-500" style={{ width: `${job.progress || 0}%` }} />
                      </div>
                    )}
                    {job.status === "failed" && (
                      <div className="mt-1 text-[11px] text-rose-300 font-semibold">Cắt thất bại — thử lại nhé.</div>
                    )}

                    <div className="mt-2 flex items-center gap-1.5">
                      {job.status === "processing" && (
                        <button
                          type="button"
                          onClick={() => stopJob(job)}
                          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg bg-rose-500/15 border border-rose-400/40 text-rose-200 text-[11px] font-bold hover:bg-rose-500/25 transition"
                          data-testid={`clip-job-stop-${job.id}`}
                        >
                          <Stop size={12} weight="fill" /> Dừng
                        </button>
                      )}
                      {job.status === "ready" && (
                        <button
                          type="button"
                          onClick={() => redownload(job)}
                          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg bg-emerald-500/20 border border-emerald-400/50 text-emerald-100 text-[11px] font-bold hover:bg-emerald-500/30 transition"
                          data-testid={`clip-job-download-${job.id}`}
                        >
                          <DownloadSimple size={12} weight="bold" /> Tải về
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => exitJob(job)}
                        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg bg-white/[0.06] border border-white/12 text-white/70 text-[11px] font-bold hover:bg-white/[0.12] transition ml-auto"
                        data-testid={`clip-job-exit-${job.id}`}
                      >
                        <X size={12} weight="bold" /> Thoát
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
